import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { BRAND } from "../../constants/brand";
import PoweredByBrand from "../../components/brand/PoweredByBrand";

const LAST_UPDATED = "25 September 2026";

const sections: { title: string; body: React.ReactNode }[] = [
  {
    title: "1. Who we are",
    body: (
      <>
        {BRAND.name} is operated by {BRAND.companyLegal} (“we”, “us”, “our”).
        This Privacy Policy explains how we collect, use, store, and share
        personal data when you use {BRAND.name} websites, admin portals, POS,
        and related services (the “Services”).
      </>
    ),
  },
  {
    title: "2. Data we collect",
    body: (
      <>
        Depending on how you use the Services, we may process:
        <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
          <li>
            <strong>Account data</strong> — name, email, phone, role, and login
            credentials for company admins and staff.
          </li>
          <li>
            <strong>Company / business data</strong> — legal name, GSTIN, PAN,
            address, and similar tax identifiers you provide.
          </li>
          <li>
            <strong>Operational data</strong> — sales, purchases, inventory,
            NFC card / wallet activity, and reports generated in your tenant.
          </li>
          <li>
            <strong>Technical data</strong> — IP address, device/browser type,
            app version, and server logs needed for security and troubleshooting.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "3. How we use data",
    body: (
      <>
        We use personal and business data to: provide and improve the Services;
        authenticate users and enforce role-based access; process billing and
        GST-related records you create; send transactional emails (e.g.
        registration OTP); prevent fraud and abuse; and meet legal or regulatory
        obligations applicable in India.
      </>
    ),
  },
  {
    title: "4. Multi-tenant isolation",
    body: (
      <>
        {BRAND.name} is multi-company. Your company’s data is scoped to your
        organisation. Platform operators may access tenant metadata as needed to
        operate the platform (for example support or abuse prevention), subject
        to internal controls.
      </>
    ),
  },
  {
    title: "5. Sharing",
    body: (
      <>
        We do not sell personal data. We may share data with infrastructure
        providers (hosting, email delivery, storage) under contracts that limit
        use to providing the Services; with your authorised staff inside your
        company; or when required by law, court order, or to protect rights and
        safety.
      </>
    ),
  },
  {
    title: "6. Retention",
    body: (
      <>
        We retain account and business records for as long as your organisation
        uses the Services and for a reasonable period afterward for backups,
        dispute resolution, and legal retention (including tax/audit needs). You
        may request deletion of account data subject to lawful retention duties.
      </>
    ),
  },
  {
    title: "7. Security",
    body: (
      <>
        We use industry-standard measures such as encrypted transport (HTTPS),
        hashed passwords, access control, and audit logging. No method of
        transmission or storage is 100% secure; please use strong passwords and
        protect staff credentials.
      </>
    ),
  },
  {
    title: "8. Your choices",
    body: (
      <>
        Staff users can update profile details where the product allows. Company
        administrators control users and business settings in their tenant. For
        access, correction, or deletion requests related to personal data we
        hold as a platform operator, contact us using the details below.
      </>
    ),
  },
  {
    title: "9. Children",
    body: (
      <>
        The Services are intended for business use by adults. We do not knowingly
        collect personal data from children under 18.
      </>
    ),
  },
  {
    title: "10. Changes",
    body: (
      <>
        We may update this Privacy Policy from time to time. The “Last updated”
        date at the top will change when we do. Continued use of the Services
        after an update means you accept the revised policy.
      </>
    ),
  },
  {
    title: "11. Contact",
    body: (
      <>
        Questions about privacy:{" "}
        <Link href={BRAND.website} target="_blank" rel="noopener noreferrer">
          {BRAND.website}
        </Link>
        {" · "}
        {BRAND.companyLegal}.
      </>
    ),
  },
];

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BRAND.colors.orange50,
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="md">
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBack />}
          sx={{ mb: 2, color: BRAND.colors.navy, fontWeight: 600 }}
        >
          Home
        </Button>

        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ color: BRAND.colors.navy, letterSpacing: "-0.02em" }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
            {BRAND.name} · Last updated {LAST_UPDATED}
          </Typography>

          <Stack spacing={2.5} divider={<Divider flexItem />}>
            {sections.map((s) => (
              <Box key={s.title}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: BRAND.colors.navy, mb: 0.75 }}
                >
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  {s.body}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 4 }}
          >
            {BRAND.copyright}
          </Typography>
          <PoweredByBrand sx={{ mt: 0.5 }} />
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
